import React from "react";
import {
  CheckCircle2,
  Wallet,
  ShoppingBag,
  RefreshCw,
  Receipt,
  ArrowRight,
  Award,
} from "lucide-react";
import { PaymentResult } from "../../types/Request/CustomerRequest";
import { PaymentTransactionType } from "../../utils/Customer/paymentConstants";

interface PaymentSuccessCardProps {
  paymentResult: PaymentResult;
  onContinue: () => void;
}

const PaymentSuccessCard: React.FC<PaymentSuccessCardProps> = ({
  paymentResult,
  onContinue,
}) => {
  // ----------------------------------------------------------
  // Header Icon Mapping
  // ----------------------------------------------------------
  const renderIcon = () => {
    switch (paymentResult.transactionType) {
      case PaymentTransactionType.WalletTopup:
        return (
          <Wallet className="w-12 h-12 text-[#2F5E2E]" strokeWidth={2.2} />
        );

      case PaymentTransactionType.RevisionPayment:
        return (
          <RefreshCw className="w-12 h-12 text-[#2F5E2E]" strokeWidth={2.2} />
        );

      default:
        return (
          <ShoppingBag className="w-12 h-12 text-[#2F5E2E]" strokeWidth={2.2} />
        );
    }
  };

  // ----------------------------------------------------------
  // Dynamic Title
  // ----------------------------------------------------------
  const renderTitle = () => {
    switch (paymentResult.transactionType) {
      case PaymentTransactionType.WalletTopup:
        return "Wallet Credited Successfully";

      case PaymentTransactionType.RevisionPayment:
        return "Revision Payment Successful";

      case PaymentTransactionType.SubscriptionRenewal:
        return "Subscription Renewed";

      default:
        return "Payment Successful";
    }
  };

  // ----------------------------------------------------------
  // Continue Button Caption
  // ----------------------------------------------------------
  const getContinueCaption = () => {
    if (paymentResult.redirectRoute === "/cartpage/customercheckoutpage") {
      return "Continue Checkout";
    }
    if (paymentResult.activeTab === "wallet") {
      return "Go to Wallet";
    }
    if (paymentResult.activeTab === "orders") {
      return "View Order Details";
    }
    return "Continue";
  };

  // Calculate loyalty points earned (1 point per 10 AED spent)
  const earnedLoyaltyPoints = Math.floor(paymentResult.paidAmount / 10);

  return (
    <div className="bg-white rounded-3xl border border-[#DDE7D8] shadow-sm p-6 sm:p-8 max-w-md mx-auto space-y-6 animate-in fade-in duration-300 selection:bg-[#A8D97B]/30 text-left">
      {/* --------------------------------------------------
          STATIC ICON HEADER BLOCK
      --------------------------------------------------- */}
      <div className="text-center space-y-3">
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-20 h-20 bg-emerald-50 rounded-full border border-emerald-100 flex items-center justify-center shadow-xs">
              {renderIcon()}
            </div>
            <CheckCircle2
              className="absolute -bottom-1 -right-1 w-6 h-6 text-emerald-600 bg-white rounded-full shadow-xs"
              strokeWidth={2.5}
            />
          </div>
        </div>

        <div className="space-y-1">
          <h2 className="text-2xl font-black text-slate-900 tracking-tight sm:text-3xl">
            {renderTitle()}
          </h2>
          <p className="text-xs text-slate-500 font-medium leading-relaxed">
            {paymentResult.successMessage ||
              "Your transaction was processed successfully!"}
          </p>
        </div>

        {paymentResult.orderNumber && (
          <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200/80 rounded-xl px-3 py-1.5 font-medium text-[11px] text-slate-600">
            <Receipt size={13} className="text-[#2F5E2E]" />
            <span>Order Reference:</span>
            <span className="font-mono font-black text-slate-900 text-xs tracking-wide">
              #{paymentResult.orderNumber}
            </span>
          </div>
        )}
      </div>

      <hr className="border-slate-100" />

      {/* --------------------------------------------------
          LOYALTY REWARDS ACCRUAL BADGE
      --------------------------------------------------- */}
      {earnedLoyaltyPoints > 0 && (
        <div className="bg-emerald-50/70 border border-emerald-100 rounded-2xl p-3.5 flex items-center gap-3">
          <div className="p-2 bg-white rounded-xl border border-emerald-200 text-emerald-700 shrink-0 shadow-2xs">
            <Award size={18} className="stroke-[2.5]" />
          </div>
          <div>
            <p className="text-[9px] font-black text-emerald-800 uppercase tracking-wider">
              Loyalty Rewards Accrual
            </p>
            <p className="text-xs font-bold text-slate-800 mt-0.5">
              Awesome! You earned{" "}
              <span className="text-emerald-700 font-mono font-black">
                +{earnedLoyaltyPoints} points
              </span>{" "}
              from this transaction.
            </p>
          </div>
        </div>
      )}

      {/* --------------------------------------------------
          PAYMENT SUMMARY RECEIPT BOX
      --------------------------------------------------- */}
      <div className="bg-slate-50/80 border border-slate-100 rounded-2xl p-4 space-y-3 text-xs">
        <div className="flex justify-between items-center text-slate-600">
          <span className="font-medium flex items-center gap-1.5">
            <ShoppingBag size={13} className="text-[#2F5E2E]" /> Paid Amount
          </span>
          <span className="font-mono font-black text-base text-[#2F5E2E]">
            AED {paymentResult.paidAmount.toFixed(2)}
          </span>
        </div>

        {paymentResult.transactionType ===
          PaymentTransactionType.WalletTopup && (
          <div className="flex justify-between items-center text-slate-600 border-t border-slate-200/60 pt-2.5">
            <span className="font-medium flex items-center gap-1.5">
              <Wallet size={13} className="text-[#2F5E2E]" /> Updated Balance
            </span>
            <span className="font-mono font-black text-sm text-slate-900">
              AED {paymentResult.walletBalance.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* --------------------------------------------------
          CONTINUE ACTION BUTTON
      --------------------------------------------------- */}
      <button
        onClick={onContinue}
        className="w-full rounded-2xl bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white py-3.5 px-6 font-black text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow-sm transition active:scale-98 cursor-pointer"
      >
        <span>{getContinueCaption()}</span>
        <ArrowRight size={14} />
      </button>
    </div>
  );
};

export default PaymentSuccessCard;